import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import {
	analyticsService,
	legalContentManager,
	networkActionsErrorsManager,
	userManager,
} from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { PtNotificationPreferencesStatus } from "@domain/user/user";
import styled from "@emotion/styled";
import { LegalContentPage } from "@ui/components/legal-content/legal-content-page";
import { RoutesPath } from "@ui/navigation/routes";
import { PrivacyNoticeLink } from "@ui/pages/register/steps/components/privacy-notice-link";
import { row } from "@ui/style/layout";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import React, { useCallback, useState } from "react";

export const LegalContent: React.FC = (): JSX.Element => {
	const { format, locale } = useI18n();
	const [isConsentChecked, setIsConsentChecked] = useState(false);
	const [isMailPhysioChecked, setIsMailPhysioChecked] = useState(false);
	const [isMailBiogenChecked, setIsMailBiogenChecked] = useState(false);

	const [consentToLegalDocuments, isLoading] = useAsyncOperation(
		useCallback(async () => {
			try {
				await legalContentManager.retrieveLegalDocuments(locale);
				await legalContentManager.consentToLegalDocuments();
				await userManager.setConsentEmails({
					physioMeEmails: isMailPhysioChecked
						? PtNotificationPreferencesStatus.ENABLED
						: PtNotificationPreferencesStatus.DISABLED,
					biogenEmails: isMailBiogenChecked
						? PtNotificationPreferencesStatus.ENABLED
						: PtNotificationPreferencesStatus.DISABLED,
				});
				await userManager.retrieveUser();
				analyticsService.trackEvent({
					category: AnalyticsCategory.PERMISSIONS,
					action: isMailBiogenChecked ? "acceptEmailConsent" : "refuseEmailConsent",
					params: ["registration", "biogen"],
				});
				analyticsService.trackEvent({
					category: AnalyticsCategory.PERMISSIONS,
					action: isMailPhysioChecked ? "acceptEmailConsent" : "refuseEmailConsent",
					params: ["registration", "physiome"],
				});
				analyticsService.trackEvent({
					category: AnalyticsCategory.REGISTRATION,
					action: "validateAdminFlow",
				});
			} catch (e) {
				console.error("Failed to retrieve or consent to the legal documents", e);
				networkActionsErrorsManager.reportError(e);
			}
		}, [isMailBiogenChecked, isMailPhysioChecked, locale]),
		{},
	);

	return (
		<LegalContentPage
			title={format("legalContent.registration.title")}
			subtitle={format("legalContent.registration.subtitle")}
		>
			<Container className="p-card">
				<div className="p-size-m">{format("legalContent.registration.form.text")}</div>
				<CheckboxesContainer>
					<CheckboxContainer>
						<StyledCheckbox
							inputId="consent"
							checked={isConsentChecked}
							onChange={() => setIsConsentChecked(current => !current)}
						/>
						<label htmlFor="consent">
							{format("global.register.consent.tou", {
								link: {
									className: "p-size-s-bold text-primary",
									onClick: event => {
										event.preventDefault();
										window.open(RoutesPath.RegisterTermsOfUse, "_blank");
									},
								},
							})}
						</label>
					</CheckboxContainer>
					<CheckboxContainer>
						<StyledCheckbox
							inputId="mailPhysio"
							checked={isMailPhysioChecked}
							onChange={() => setIsMailPhysioChecked(current => !current)}
						/>
						<label htmlFor="mailPhysio">{format("global.register.physiomeEmails.v1_6")}</label>
					</CheckboxContainer>
					<CheckboxContainer>
						<StyledCheckbox
							inputId="mailBiogen"
							checked={isMailBiogenChecked}
							onChange={() => setIsMailBiogenChecked(current => !current)}
						/>
						<label htmlFor="mailBiogen">{format("global.register.biogenEmails.v1_6")}</label>
					</CheckboxContainer>
				</CheckboxesContainer>
				<StyledButton
					label={format("legalContent.registration.form.continueButton")}
					disabled={!isConsentChecked}
					onClick={() => {
						consentToLegalDocuments();
					}}
					loading={isLoading}
				/>
				<PrivacyNoticeLink />
			</Container>
		</LegalContentPage>
	);
};

const CheckboxContainer = styled.div`
	${row(undefined, undefined, "center")}
`;

const CheckboxesContainer = styled.div`
	display: grid;
	gap: 8px;
`;

const StyledCheckbox = styled(Checkbox)`
	margin-right: 1rem;
`;

const StyledButton = styled(Button)`
	margin-top: 16px;
	width: 100%;
`;

const Container = styled.div`
	display: grid;
	gap: 16px;
	padding: 32px;
	width: min(calc(100vw - 40px), 496px);
`;
